// Las gráficas de pólizas de los Paneles de Reportes.
//
// Los números vienen hechos de panelDePolizas.js; esto solo los pinta. Cinco
// formas: indicador (con comparación contra el período anterior y una
// mini-línea), barras, línea por mes, dona y tabla. En las cuatro últimas, un
// clic en una barra, un pedazo o una fila abre las pólizas de ese grupo
// (`onDrill`) — "¿quiénes son esas 14?" se contesta sin salir del panel.
//
// Los colores van con las variables del tema (--c-*) donde el navegador las
// acepta (estilos), y con tonos que se leen en claro y en oscuro donde Recharts
// los pone como atributo SVG.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LabelList,
  AreaChart, Area, PieChart, Pie, Cell,
} from 'recharts';
import { TrendingUp, TrendingDown, Minus, ChevronDown, Check, Search } from 'lucide-react';
import { formatoDe, MEDIDAS_QUE_NO_SUMAN, datosDeLaGrafica } from '../panelDePolizas.js';

export const PALETA = ['#00b4d8', '#8b5cf6', '#10b981', '#f59e0b', '#ec4899', '#3b82f6', '#f97316', '#14b8a6'];
const GRIS_OTROS = '#64748b';
const EJE = '#7f93a8';
const REJILLA = 'rgba(127,147,168,.18)';
const MAX_BARRAS = 15;
const MAX_PEDAZOS = 8;

export const ETIQUETAS_DE_MEDIDA = {
  policies: { es: 'Pólizas', en: 'Policies' },
  premium: { es: 'Prima mensual', en: 'Monthly premium' },
  annualPremium: { es: 'Prima anual', en: 'Annual premium' },
  commission: { es: 'Comisión', en: 'Commission' },
  clients: { es: 'Clientes', en: 'Clients' },
  members: { es: 'Miembros', en: 'Members' },
};
export const ETIQUETAS_DE_DIMENSION = {
  none: { es: 'Sin agrupar (un número)', en: 'No grouping (one number)' },
  agent: { es: 'Agente', en: 'Agent' },
  analyst: { es: 'Analista', en: 'Analyst' },
  state: { es: 'Estado (US)', en: 'State' },
  carrier: { es: 'Compañía', en: 'Carrier' },
  type: { es: 'Tipo de póliza', en: 'Policy type' },
  status: { es: 'Estatus', en: 'Status' },
  renewalStatus: { es: 'Status de renovación', en: 'Renewal status' },
  month: { es: 'Mes', en: 'Month' },
};

const L = (o, lang) => (o ? (lang === 'es' ? o.es : o.en) : '');

/** El título que sale si la gráfica no tiene uno propio: "Comisión por agente". */
export function tituloPorDefecto(w, lang) {
  const med = L(ETIQUETAS_DE_MEDIDA[w.measure || 'policies'], lang);
  if (!w.dimension || w.dimension === 'none') return med;
  return `${med} ${lang === 'es' ? 'por' : 'by'} ${L(ETIQUETAS_DE_DIMENSION[w.dimension], lang).toLowerCase()}`;
}

const fechaCorta = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || '');
  if (!m) return '';
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
export const textoDeRango = (r, lang) => {
  if (!r?.from && !r?.to) return lang === 'es' ? 'Todo el tiempo' : 'All time';
  if (r.from && r.to) return `${fechaCorta(r.from)} – ${fechaCorta(r.to)}`;
  return r.from ? `${lang === 'es' ? 'Desde' : 'From'} ${fechaCorta(r.from)}` : `${lang === 'es' ? 'Hasta' : 'Until'} ${fechaCorta(r.to)}`;
};

// Una FUNCIÓN y no un componente: Recharts la llama con { active, payload }
// por su cuenta, y un componente que lee props que ningún sitio le pasa es
// justo lo que vigila propsQueNadiePasa.test.ts.
const burbuja = (medida, total, lang) => function contenido({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const pct = total > 0 && !MEDIDAS_QUE_NO_SUMAN.has(medida) ? Math.round((d.value / total) * 1000) / 10 : null;
  return (
    <div style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, padding: '.55rem .75rem', boxShadow: '0 8px 24px rgba(0,0,0,.25)', fontSize: 12 }}>
      <div style={{ color: 'var(--c-text2,#7aa3c0)', fontWeight: 600, marginBottom: 3 }}>{d.name}</div>
      <div style={{ color: 'var(--c-white,#eef6ff)', fontWeight: 800, fontSize: 14 }}>
        {formatoDe(medida, d.value)}
        {pct !== null && <span style={{ color: 'var(--c-text3,#45688a)', fontWeight: 600, fontSize: 11, marginLeft: 6 }}>{pct}%</span>}
      </div>
      {!d._otros && d.polizas && (
        <div style={{ color: 'var(--c-text3,#45688a)', fontSize: 10.5, marginTop: 3 }}>
          {lang === 'es' ? 'Clic para ver las pólizas' : 'Click to see the policies'}
        </div>
      )}
    </div>
  );
};

/** Junta la cola en "Otros" para que no haya colores que nadie distingue. */
function conOtros(filas, max, lang) {
  if (filas.length <= max) return filas;
  const cabeza = filas.slice(0, max - 1);
  const cola = filas.slice(max - 1);
  return [...cabeza, {
    clave: '__otros__', name: lang === 'es' ? `Otros (${cola.length})` : `Other (${cola.length})`,
    value: Math.round(cola.reduce((s, f) => s + f.value, 0) * 100) / 100,
    polizas: cola.flatMap((f) => f.polizas), _otros: true,
  }];
}

// ── Indicador ────────────────────────────────────────────────────────────
function Indicador({ widget, datos, polizas, clientes, lang, rangoDelPanel, filtrosDelPanel, color }) {
  const es = lang === 'es';
  const medida = datos.medida;
  // Mini-línea de los últimos meses debajo del número: la tendencia de un
  // vistazo, con la misma receta y agrupada por mes.
  const serie = useMemo(() => {
    const s = datosDeLaGrafica({ ...widget, dimension: 'month' }, { polizas, clientes, lang, rangoDelPanel, filtrosDelPanel });
    return s.filas.slice(-12);
  }, [widget, polizas, clientes, lang, rangoDelPanel, filtrosDelPanel]);
  const sube = datos.cambio !== null && datos.cambio > 0;
  const baja = datos.cambio !== null && datos.cambio < 0;
  const tono = sube ? '#10b981' : baja ? '#ef4444' : 'var(--c-text3,#45688a)';
  const Icono = sube ? TrendingUp : baja ? TrendingDown : Minus;
  return (
    <div>
      <div style={{ fontSize: '2.1rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)', fontFamily: 'var(--font-display)', lineHeight: 1.1, letterSpacing: '-.01em' }}>
        {formatoDe(medida, datos.total, { corto: datos.total >= 100000 })}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, minHeight: 20, flexWrap: 'wrap' }}>
        {datos.anterior !== null ? (
          <>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '.1rem .45rem', borderRadius: 999, background: sube ? 'rgba(16,185,129,.14)' : baja ? 'rgba(239,68,68,.14)' : 'var(--c-surface2,#112538)', color: tono, fontSize: '.72rem', fontWeight: 800 }}>
              <Icono size={12} /> {datos.cambio === null ? (es ? 'nuevo' : 'new') : `${datos.cambio > 0 ? '+' : ''}${datos.cambio}%`}
            </span>
            <span style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)' }}>
              {es ? 'vs. período anterior' : 'vs. previous period'} ({formatoDe(medida, datos.anterior, { corto: true })})
            </span>
          </>
        ) : (
          <span style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)' }}>
            {es ? 'Elige un rango de fechas para comparar' : 'Pick a date range to compare'}
          </span>
        )}
      </div>
      {serie.length > 1 && (
        <div style={{ height: 46, marginTop: 8 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={serie} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={`kpi-${widget.id}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Tooltip content={burbuja(medida, 0, lang)} cursor={false} />
              <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2} fill={`url(#kpi-${widget.id})`} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// ── Tabla ────────────────────────────────────────────────────────────────
function Tabla({ filas, medida, total, lang, onDrill, dimension }) {
  const es = lang === 'es';
  const max = Math.max(1, ...filas.map((f) => f.value));
  const reparte = !MEDIDAS_QUE_NO_SUMAN.has(medida) && total > 0;
  return (
    <div style={{ maxHeight: 360, overflowY: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.78rem' }}>
        <thead>
          <tr>
            <th style={th}>{L(ETIQUETAS_DE_DIMENSION[dimension], lang)}</th>
            <th style={{ ...th, textAlign: 'right' }}>{L(ETIQUETAS_DE_MEDIDA[medida], lang)}</th>
            {reparte && <th style={{ ...th, textAlign: 'right', width: 120 }}>%</th>}
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => (
            <tr key={f.clave} onClick={() => onDrill?.(f)} className="dkt-panel-fila" title={es ? 'Ver las pólizas' : 'See the policies'}>
              <td style={{ padding: '.45rem .5rem', color: 'var(--c-text,#c8dff0)', fontWeight: 600 }}>{f.name}</td>
              <td style={{ padding: '.45rem .5rem', textAlign: 'right', fontWeight: 800, color: 'var(--c-white,#eef6ff)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{formatoDe(medida, f.value)}</td>
              {reparte && (
                <td style={{ padding: '.45rem .5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                    <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--c-surface2,#112538)', overflow: 'hidden', maxWidth: 70 }}>
                      <div style={{ width: `${(f.value / max) * 100}%`, height: '100%', background: PALETA[0], borderRadius: 3 }} />
                    </div>
                    <span style={{ fontSize: '.7rem', color: 'var(--c-text2,#7aa3c0)', minWidth: 36, textAlign: 'right' }}>{Math.round((f.value / total) * 1000) / 10}%</span>
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
const th = { textAlign: 'left', padding: '.4rem .5rem', fontSize: '.66rem', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--c-text3,#45688a)', borderBottom: '1px solid var(--c-border,#1a3550)', position: 'sticky', top: 0, background: 'var(--c-surface,#0d1c2e)' };

// ── La gráfica ───────────────────────────────────────────────────────────
export function PanelChart({ widget, datos, polizas, clientes, lang, rangoDelPanel, filtrosDelPanel, onDrill, color = PALETA[0] }) {
  const es = lang === 'es';
  const tipo = widget.chartType || 'bar';
  const { medida, filas, total } = datos;
  const sumaFilas = filas.reduce((s, f) => s + f.value, 0);

  if (tipo === 'kpi' || datos.dimension === 'none') {
    return <Indicador widget={widget} datos={datos} polizas={polizas} clientes={clientes} lang={lang} rangoDelPanel={rangoDelPanel} filtrosDelPanel={filtrosDelPanel} color={color} />;
  }
  if (!filas.length || filas.every((f) => !f.value)) {
    return (
      <div style={{ padding: '2rem 1rem', textAlign: 'center', color: 'var(--c-text3,#45688a)', fontSize: '.8rem' }}>
        {es ? 'No hay pólizas con estos filtros.' : 'No policies match these filters.'}
      </div>
    );
  }
  const clic = (f) => { if (f && !f._otros) onDrill?.(f); };

  if (tipo === 'table') return <Tabla filas={filas} medida={medida} total={sumaFilas} lang={lang} onDrill={clic} dimension={datos.dimension} />;

  if (tipo === 'line') {
    return (
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={filas} margin={{ top: 10, right: 12, bottom: 0, left: 0 }} onClick={(e) => clic(e?.activePayload?.[0]?.payload)}>
          <defs>
            <linearGradient id={`ln-${widget.id}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.35} />
              <stop offset="100%" stopColor={color} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={REJILLA} vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: EJE }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={12} />
          <YAxis tick={{ fontSize: 11, fill: EJE }} tickLine={false} axisLine={false} width={52} tickFormatter={(v) => formatoDe(medida, v, { corto: true })} allowDecimals={false} />
          <Tooltip content={burbuja(medida, 0, lang)} cursor={{ stroke: color, strokeOpacity: 0.3 }} />
          <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2.5} fill={`url(#ln-${widget.id})`} dot={{ r: 3, fill: color, strokeWidth: 0 }} activeDot={{ r: 6, style: { cursor: 'pointer' } }} />
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  if (tipo === 'donut' && !MEDIDAS_QUE_NO_SUMAN.has(medida) && filas.every((f) => f.value >= 0)) {
    const pedazos = conOtros(filas.filter((f) => f.value > 0), MAX_PEDAZOS, lang);
    const suma = pedazos.reduce((s, f) => s + f.value, 0);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', width: 200, height: 200, flex: '0 0 auto', margin: '0 auto' }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={pedazos} dataKey="value" nameKey="name" innerRadius="62%" outerRadius="96%" paddingAngle={2} stroke="none" onClick={(d) => clic(d?.payload || d)} style={{ cursor: 'pointer' }}>
                {pedazos.map((d, i) => <Cell key={d.clave} fill={d._otros ? GRIS_OTROS : PALETA[i % PALETA.length]} />)}
              </Pie>
              <Tooltip content={burbuja(medida, suma, lang)} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none', textAlign: 'center' }}>
            <div>
              <div style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)', fontFamily: 'var(--font-display)' }}>{formatoDe(medida, suma, { corto: true })}</div>
              <div style={{ fontSize: '.65rem', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Total</div>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 160px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {pedazos.map((d, i) => (
            <button key={d.clave} type="button" onClick={() => clic(d)} className="dkt-panel-leyenda" disabled={d._otros}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: d._otros ? GRIS_OTROS : PALETA[i % PALETA.length], flex: '0 0 auto' }} />
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>{d.name}</span>
              <span style={{ fontWeight: 800, color: 'var(--c-white,#eef6ff)' }}>{formatoDe(medida, d.value, { corto: true })}</span>
              <span style={{ color: 'var(--c-text3,#45688a)', minWidth: 38, textAlign: 'right' }}>{suma ? Math.round((d.value / suma) * 1000) / 10 : 0}%</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Barras. Por mes van de pie (una línea de tiempo se lee de izquierda a
  // derecha); por persona o grupo, acostadas y de mayor a menor.
  if (datos.dimension === 'month') {
    return (
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={filas} margin={{ top: 18, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={REJILLA} vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: EJE }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={8} />
          <YAxis tick={{ fontSize: 11, fill: EJE }} tickLine={false} axisLine={false} width={52} tickFormatter={(v) => formatoDe(medida, v, { corto: true })} allowDecimals={false} />
          <Tooltip content={burbuja(medida, 0, lang)} cursor={{ fill: 'rgba(127,147,168,.08)' }} />
          <Bar dataKey="value" fill={color} radius={[5, 5, 0, 0]} maxBarSize={42} onClick={(d) => clic(d?.payload || d)} style={{ cursor: 'pointer' }} />
        </BarChart>
      </ResponsiveContainer>
    );
  }
  const barras = conOtros(filas, MAX_BARRAS, lang);
  const alto = Math.max(160, barras.length * 32 + 24);
  return (
    <ResponsiveContainer width="100%" height={alto}>
      <BarChart data={barras} layout="vertical" margin={{ top: 0, right: 64, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id={`br-${widget.id}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={color} stopOpacity={0.55} />
            <stop offset="100%" stopColor={color} stopOpacity={1} />
          </linearGradient>
        </defs>
        <XAxis type="number" hide />
        <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11.5, fill: EJE }} tickLine={false} axisLine={false} />
        <Tooltip content={burbuja(medida, sumaFilas, lang)} cursor={{ fill: 'rgba(127,147,168,.08)' }} />
        <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={18} onClick={(d) => clic(d?.payload || d)} style={{ cursor: 'pointer' }} background={{ fill: 'rgba(127,147,168,.07)', radius: 6 }}>
          {barras.map((d) => <Cell key={d.clave} fill={d._otros ? GRIS_OTROS : `url(#br-${widget.id})`} />)}
          <LabelList dataKey="value" position="right" formatter={(v) => formatoDe(medida, v, { corto: true })} style={{ fill: EJE, fontSize: 11.5, fontWeight: 700 }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Filtro de varias opciones ────────────────────────────────────────────
/**
 * Un botón "Agente: Todos ▾" que abre una lista con casillas y buscador.
 * `value` = lista de claves elegidas; vacía = todos.
 */
export function FiltroMultiple({ etiqueta, opciones, value = [], onChange, lang, compacto = false }) {
  const es = lang === 'es';
  const [abierto, setAbierto] = useState(false);
  const [busca, setBusca] = useState('');
  const caja = useRef(null);
  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => { if (caja.current && !caja.current.contains(e.target)) setAbierto(false); };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto]);
  const elegidas = value.filter((v) => opciones.some((o) => o.value === v));
  const resumen = elegidas.length === 0
    ? (es ? 'Todos' : 'All')
    : elegidas.length === 1 ? (opciones.find((o) => o.value === elegidas[0])?.label || elegidas[0]) : `${elegidas.length}`;
  const visibles = opciones.filter((o) => !busca || o.label.toLowerCase().includes(busca.toLowerCase()));
  const cambia = (v) => onChange(elegidas.includes(v) ? elegidas.filter((x) => x !== v) : [...elegidas, v]);
  return (
    <div ref={caja} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setAbierto((a) => !a)} className={`dkt-panel-filtro${elegidas.length ? ' on' : ''}`} style={compacto ? { fontSize: '.72rem', padding: '.3rem .55rem' } : undefined}>
        <span style={{ color: 'var(--c-text3,#45688a)', fontWeight: 600 }}>{etiqueta}:</span>
        <span style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{resumen}</span>
        <ChevronDown size={13} />
      </button>
      {abierto && (
        <div className="dkt-panel-pop">
          {opciones.length > 6 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '.35rem .5rem', borderBottom: '1px solid var(--c-border,#1a3550)' }}>
              <Search size={13} color="var(--c-text3,#45688a)" />
              <input autoFocus value={busca} onChange={(e) => setBusca(e.target.value)} placeholder={es ? 'Buscar…' : 'Search…'} style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'var(--c-white,#eef6ff)', fontSize: '.78rem', fontFamily: 'inherit' }} />
            </div>
          )}
          <div style={{ maxHeight: 240, overflowY: 'auto', padding: '.25rem' }}>
            {visibles.length === 0 && (
              <div style={{ padding: '.6rem', fontSize: '.75rem', color: 'var(--c-text3,#45688a)' }}>{es ? 'Sin opciones' : 'No options'}</div>
            )}
            {visibles.map((o) => {
              const on = elegidas.includes(o.value);
              return (
                <button key={o.value} type="button" onClick={() => cambia(o.value)} className="dkt-panel-opcion">
                  <span className={`dkt-panel-check${on ? ' on' : ''}`}>{on && <Check size={11} />}</span>
                  <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
                </button>
              );
            })}
          </div>
          {elegidas.length > 0 && (
            <button type="button" onClick={() => onChange([])} style={{ width: '100%', padding: '.45rem', background: 'none', border: 'none', borderTop: '1px solid var(--c-border,#1a3550)', color: 'var(--c-cyan,#00d4ff)', fontSize: '.74rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              {es ? 'Quitar filtro' : 'Clear filter'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
